import { KenticoKontent } from "./KenticoKontent";

export interface InputItem {
	modules: Module[];
	name: string;
}

interface Content {
	modules?: Module[];
	name: string;
	urls?: string[];
	value?: string;
}

interface Module {
	contents?: Content[];
	name: string;
}

type MappedOutput = Record<string, any>;

function sanitizeName(input: string): string {
	const textarea = document.createElement("textarea");
	textarea.innerHTML = input;
	const sanitized = textarea.value.trim();
	return toCamelCase(sanitized);
}

function sanitizeValue(input: string): string {
	const textarea = document.createElement("textarea");
	textarea.innerHTML = input;
	return textarea.value.trim();
}

function toCamelCase(str: string): string {
	return str
		.replace(/[^a-zA-Z0-9]+/g, " ")
		.replace(/\w+/g, (word, index) =>
			index > 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()
		)
		.replace(/\s+/g, "");
}

function processmodules(modules: Module[], keepNames: boolean): MappedOutput {
	const moduleResult: MappedOutput = {};

	for (const module of modules) {
		const sanitizedModuleName = sanitizeName(module.name);
		const match = sanitizedModuleName.match(/^([a-zA-Z_]+?)(\d+)(.*)$/);

		if (match && keepNames) {
			const [, baseName, index, suffix] = match;
			const key = sanitizeName(baseName);
			const idx = parseInt(index, 10) - 1;

			moduleResult[key] ??= [];
			while (moduleResult[key].length <= idx) {
				moduleResult[key].push({});
			}

			const target = moduleResult[key][idx];

			if (suffix) {
				const suffixKey = sanitizeName(suffix);
				target[suffixKey] = {};
			}

			if (module.contents?.length) {
				target.contents = processContents(module.contents);
			}
		} else if (keepNames) {
			const entry: MappedOutput = {};

			if (module.contents?.length) {
				entry.contents = processContents(module.contents);
			}

			moduleResult[sanitizedModuleName] = entry;
		} else {
			// Drop names for nested modules
			if (module.contents?.length) {
				const entry = processContents(module.contents);
				moduleResult.contents ??= [];
				moduleResult.contents.push(entry);
			}
		}
	}

	return moduleResult;
}

function processContents(contents: Content[]): MappedOutput {
	const contentResult: MappedOutput = {};

	for (const content of contents) {
		const sanitizedContentName = sanitizeName(content.name);
		const match = sanitizedContentName.match(/^([a-zA-Z_]+?)(\d+)(.*)$/);

		if (match) {
			const [, baseName, index, suffix] = match;
			const key = sanitizeName(baseName);
			const idx = parseInt(index, 10) - 1;

			contentResult[key] ??= [];
			while (contentResult[key].length <= idx) {
				contentResult[key].push({});
			}

			const target = contentResult[key][idx];

			if (suffix) {
				const suffixKey = sanitizeName(suffix);
				target[suffixKey] = extractValue(content);
			} else {
				Object.assign(target, extractValue(content));
			}

			if (content.modules?.length) {
				target.modules = processmodules(content.modules, false);
			}
		} else {
			const entry: MappedOutput = extractValue(content);

			if (content.modules?.length) {
				entry.modules = processmodules(content.modules, false);
			}

			contentResult[sanitizedContentName] = entry;
		}
	}

	return contentResult;
}

function extractValue(content: Content) {
	const result: Record<string, any> = {};
	if (content.value !== undefined) result.value = sanitizeValue(content.value);
	if (content.urls !== undefined) result.urls = content.urls.map((url) => sanitizeValue(url));
	return result;
}

function mapInputItems(inputItems: InputItem[]): MappedOutput {
	const result: MappedOutput = {};

	for (const item of inputItems) {
		const sanitizedItemName = sanitizeName(item.name);
		result[sanitizedItemName] = processmodules(item.modules, true);
	}

	return result;
}

export const mapToKenticoKontent = (data: InputItem[]): KenticoKontent => {
	const result = mapInputItems(data) as KenticoKontent;
	return result;
};
